import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import error_icon from "../../images/error_icon.png";
import {
  selectLoading,
  setIsLoading,
  stepperRank,
  vibrationPopUp,
} from "../../reducer/onBoardSlice";
import { useDispatch } from "react-redux";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import {
  login,
  setCurrentFirmware,
  setNextFirmware,
  setUserLoadData,
  userToken,
} from "../../reducer/userSlice";
import Loading from "../../shared/Loading-screen/Loading";
import { setNotify } from "../../reducer/notifySlice";
import { notifyTypes, stepsToDevice } from "../../utils/constants";
import { isEmpty } from "../../utils/_helperFunction";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const DIRECTUS_BASE = process.env.REACT_APP_DIRECTUS_URL;

const initState = {
  emailAddress: "",
  password: "",
  keepLoggedIn: false,
};

async function directusLoginAndMe(email, password) {
  const res = await fetch(`${DIRECTUS_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const loginJson = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = loginJson?.errors?.[0]?.message || "Login failed";
    throw new Error(msg);
  }
  const { access_token: accessToken, refresh_token: refreshToken } =
    loginJson?.data || {};
  if (!accessToken) throw new Error("No access token returned by Directus");

  // 2) Me
  const meRes = await fetch(
    `${DIRECTUS_BASE}/users/me?fields=id,email,first_name,last_name,role.*,status`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  const meJson = await meRes.json().catch(() => ({}));
  if (!meRes.ok) {
    const msg = meJson?.errors?.[0]?.message || "Failed to fetch current user";
    throw new Error(msg);
  }
  const me = meJson?.data || meJson;
  return { accessToken, refreshToken, me };
}

async function fetchDirectusUserLoadData(accessToken) {
  if (!DIRECTUS_BASE) {
    return null;
  }

  const fields = ["id", "Date", ...stepsToDevice];
  const url = new URL(`${DIRECTUS_BASE}/items/User_Configuration`);
  url.searchParams.set("sort", "-OnCreate");
  url.searchParams.set("limit", "1");
  url.searchParams.set("fields", fields.join(","));
  const res = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json?.errors?.[0]?.message || "Failed to load saved data";
    throw new Error(msg);
  }

  const rows = Array.isArray(json?.data) ? json.data : [];
  const content = rows.map((row) => {
    const data = {};
    stepsToDevice.forEach((field) => {
      const value = row[field];
      if (value !== undefined && value !== null) {
        data[field] = { value };
      }
    });
    return { data };
  });

  return {
    totalElements: rows.length,
    size: rows.length,
    content,
  };
}

const LoginModal = ({ server }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoading = useSelector(selectLoading);
  const [error, setError] = React.useState(false);
  const [state, setState] = React.useState(initState);
  const [isPasswordShow, setShowPassword] = React.useState(false);
  const [isCapsShow, setShowCaps] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword(!isPasswordShow);

  const { emailAddress, password, keepLoggedIn } = state;

  const onSignUpClick = () => {
    if (isLoading) {
      return;
    }
    dispatch(stepperRank(0.2));
  };

  const onForgotPasswordClick = async () => {
    if (!emailAddress) {
      return;
    }
    // Directus appends ?token=... to this URL; keep it allow-listed on the API side.
    const resetUrl = `${window.location.origin}/?type=pr`;
    try {
      const res = await fetch(`${DIRECTUS_BASE}/auth/password/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailAddress, reset_url: resetUrl }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.errors?.[0]?.message || "Failed to request reset";
        throw new Error(msg);
      }
      dispatch(
        setNotify({
          message: `Reset link sent. Please check ${emailAddress}.`,
          type: notifyTypes.INFO,
          duration: 3000,
        })
      );
    } catch (error) {
      dispatch(
        setNotify({
          message: String(error?.message || error),
          type: notifyTypes.ERROR,
          duration: 3000,
        })
      );
    }
  };

  const handleCapsLock = (event) => {
    if (!event.getModifierState("CapsLock")) {
      if (isCapsShow) {
        setShowCaps(false);
        setError("");
      }
      return;
    } else {
      setShowCaps(true);
      setError("Caps Lock is on");
    }
  };

  const handleChange = (event) => {
    if (isLoading) {
      return;
    }
    const name = event.target.name;
    let value = event.target.value;
    if (name === "keepLoggedIn") {
      value = !keepLoggedIn;
    }
    setState((prev) => ({ ...prev, [name]: value }));
    if (error) {
      setError(false);
    }
  };

  const startWork = () => {
    dispatch(vibrationPopUp(false));
    dispatch(stepperRank(0));
    navigate("/onboarding");
    dispatch(
      setNotify({
        message: "Logged in successfully!",
        type: notifyTypes.SUCCESS,
        duration: 3000,
      })
    );
  };

  const onSubmit = async () => {
    if (
      password.length < 6 ||
      !emailAddress.includes("@") ||
      emailAddress.length < 4
    ) {
      setError(true);
      return;
    }

    dispatch(setIsLoading(true));
    try {
      // ---- Directus login + user ----
      const { accessToken, me } = await directusLoginAndMe(
        emailAddress,
        password
      );

      dispatch(userToken({ token: accessToken }));

      const fullName =
        [me?.first_name, me?.last_name].filter(Boolean).join(" ") ||
        me?.email ||
        "";
      const userRole = me?.role?.name || "user";
      const status = me?.status || "active";

      dispatch(
        login({
          userId: me?.id,
          userName: fullName,
          userMail: me?.email,
          userRole,
          canViewSensitiveData: false,
          status,
        })
      );

      try {
        const savedData = await fetchDirectusUserLoadData(accessToken);
        const content = savedData?.content || [];
        const lastEntry = content[content.length - 1]?.data;
        const checkArray = [...stepsToDevice];
        let hasAllFields =
          !!lastEntry && !isEmpty(lastEntry) && !checkArray.some(
            (field) => !lastEntry[field] || isEmpty(lastEntry[field])
          );
        if (hasAllFields) {
          dispatch(setUserLoadData(savedData));
        }
      } catch (loadError) {
        console.log("directus_load_previous_data_ERROR", loadError);
      }

      startWork();
    } catch (error) {
      console.error("directus_login_ERROR", error);
      dispatch(
        setNotify({
          message: String(error?.message || error),
          type: notifyTypes.ERROR,
          duration: 3000,
        })
      );
    }
    dispatch(setIsLoading(false));
  };
  return (
    <div className="homeScreenPopoup" style={{ padding: "2rem 2rem 0 2rem" }}>
      <div className="popUp-content">
        <div
          className="popUp-body"
          style={{ alignItems: "flex-start", padding: "1rem 2rem 0 2rem" }}
        >
          <div className="account-title" style={{ marginBottom: "45px" }}>
            <h3>Log In</h3>
          </div>
          <div className="account-control">
            <TextField
              label="Email"
              variant="standard"
              name="emailAddress"
              type="email"
              placeholder="Email"
              value={emailAddress}
              onKeyUp={handleCapsLock}
              onChange={handleChange}
              fullWidth
            />
          </div>
          <div className="account-control">
            <TextField
              label="Password"
              variant="standard"
              name="password"
              placeholder="Password"
              type={isPasswordShow ? "text" : "password"}
              value={password}
              onKeyUp={handleCapsLock}
              onChange={handleChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      // onMouseLeave={handleMouseDownPassword}
                    >
                      {isPasswordShow ? (
                        <Visibility style={{ outline: "none" }} />
                      ) : (
                        <VisibilityOff style={{ outline: "none" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
                // startAdornment: (
                //     <>
                //         {
                //             isCapsShow ? (
                //                 <InputAdornment position="start">
                //                 <IconButton>
                //                     <img src={error_icon} alt='IconButton'/>
                //                 </IconButton>
                //             </InputAdornment>
                //             ) : null
                //         }
                //     </>
                // )
              }}
            />
          </div>
          {error ? (
            <div className="account-error">
              <img
                src={error_icon}
                style={{ margin: "0 1rem" }}
                alt="error_icon"
              />
              <p
                style={{
                  color: "#E11D48",
                  fontWeight: "500",
                  marginTop: "0.7rem",
                }}
              >
                {error?.length
                  ? error
                  : "Incorrect email or password! Try again."}
              </p>
            </div>
          ) : (
            <div style={{ height: "55px" }}></div>
          )}
          <div className="forgot-password">
            {/* <input
                            type="checkbox"
                            name="keepLoggedIn"
                            value={keepLoggedIn}
                            onChange={handleChange}
                        />
                        Keep me logged in */}
            <span
              title="Make sure Email is not empty"
              disabled={!emailAddress}
              onClick={
                emailAddress
                  ? onForgotPasswordClick
                  : () =>
                      dispatch(
                        setNotify({
                          message: "Make sure Email is not empty",
                          type: notifyTypes.WARNING,
                          duration: 3000,
                        })
                      )
              }
              style={{
                color: emailAddress ? "#3840D7" : "grey",
                cursor: emailAddress ? "pointer" : "default",
                marginLeft: "390px",
              }}
            >
              Forgot Password?
            </span>
          </div>
          <button
            onClick={onSubmit}
            className="button-blue"
            disabled={isLoading}
            style={{ marginTop: "20px", width: "100%" }}
          >
            Log In
          </button>
          <div className="have-account">
            <p style={{ margin: "10px 0 20px 0" }}>
              Don’t have an account?{" "}
              <span
                onClick={onSignUpClick}
                style={{ color: "#3840D7", cursor: "pointer" }}
              >
                Sign Up
              </span>
            </p>
          </div>
        </div>
      </div>
      {isLoading && <Loading isInBlock />}
    </div>
  );
};

export default LoginModal;
